/**
 * @typedef {Object} Price
 * @property {string} amount - The price amount as a string.
 * @property {string} currencyCode - The currency code for the price.
 */

/**
 * @typedef {Object} QuantityPriceBreak
 * @property {number} minimumQuantity - The minimum quantity for this price break.
 * @property {Price} price - The price associated with this quantity.
 */

/**
 * @typedef {Object} QuantityRule
 * @property {number} increment - The quantity increment for ordering.
 * @property {number} minimum - The minimum quantity allowed.
 * @property {number} maximum - The maximum quantity allowed.
 */

/**
 * @typedef {Object} ProductVariant
 * @property {string} id - The unique ID of the product variant.
 * @property {string} title - The title of the product variant (e.g., expiration date).
 * @property {Price} price - The price of the product variant.
 * @property {QuantityPriceBreak[]} quantityPriceBreaks - The list of quantity price breaks.
 * @property {QuantityRule} quantityRule - The rules for quantity increments and limits.
 */

/**
 * @typedef {Object} ProductVolumeData
 * @property {string} id - The unique ID of the product.
 * @property {string} title - The title of the product.
 * @property {string} status - The status of the product (e.g., ACTIVE).
 * @property {ProductVariant[]} variants - The list of product variants.
 */

/**
 * Fetches volume pricing info from the server, returns null if not found
 * @param {string} productId
 * @returns {ProductVolumeData | null}
 */
const getVolumePricing = async (productId) => {
  try {
    const request = await fetch("https://chc.sh/volume-pricing?productId=" + productId, {
      headers: { Accept: "application/json" },
      method: "GET",
    });
    const responseArray = await request.json();

    if (Array.isArray(responseArray)) {
      return responseArray[0] || null;
    } else {
      return null;
    }
  } catch (err) {
    console.error(err);
    return null;
  }
};

/**
 * Converts an ISO 8601 date string to a formatted date
 * @param {string} dateString
 * @returns
 */
const formatDate = (dateString) => {
  const date = new Date(dateString);

  const options = { year: "numeric", month: "short", day: "numeric" };
  return date.toLocaleDateString("en-GB", options);
};

const getWeightUnit = (weight, unitKind) => {
  switch (unitKind) {
    case "g":
      if (weight < 1000) {
        return "g";
      } else {
        return "kg";
      }
    case "ml":
      if (weight < 1000) {
        return "ml";
      } else {
        return "L";
      }
    case "L":
      if (weight < 1000) {
        return "L";
      } else {
        return "kL";
      }
    case "kg":
      if (weight < 1000) {
        return "kg";
      } else {
        return "t";
      }
    case "pc":
      return "pc";
    case "unit":
      return "unit";
    default:
      return "g";
  }
};

/**
 * Converts an amount to a unit string (gr or kg) depending on the value.
 * @param {number} amount
 * @returns {string}
 */
const convertAmountToAmoutWithUnit = (amount, baseWeightUnit) => {
  if (amount < 1000) {
    return `${amount + getWeightUnit(amount, baseWeightUnit)}`;
  } else {
    const amountInKg = (amount / 1000).toFixed(2);
    if (amountInKg.search(".00") !== -1) {
      return `${amountInKg.replace(".00", "") + getWeightUnit(amount, baseWeightUnit)}`;
    } else {
      return `${amountInKg + getWeightUnit(amount, baseWeightUnit)}`;
    }
  }
};

/**
 * Finds the lowest price from the quantity price breaks across all variants.
 * @param {ProductVariant[]} variants - The list of product variants.
 * @param {number} currencyRate - The conversion rate to the active currency.
 * @returns {number} - The lowest price found.
 */
const findLowestPrice = (variants, currencyRate) => {
  let lowestPrice = Infinity;

  // Loop through all variants and their quantityPriceBreaks
  variants.forEach((variant) => {
    variant.quantityPriceBreaks.forEach((breakPoint) => {
      const priceInActiveCurrency = breakPoint.price.amount * currencyRate;
      if (priceInActiveCurrency < lowestPrice) {
        lowestPrice = priceInActiveCurrency;
      }
    });
  });

  return lowestPrice;
};

/**
 * Mapping of currency codes to common locales for the specified markets.
 */
const currencyToLocaleMap = {
  DKK: "da-DK", // Denmark (Danish Krone)
  CZK: "cs-CZ", // Czechia (Czech Koruna)
  EUR: "en-DE", // EU (Euro, commonly used in Germany)
  USD: "en-US", // United States (US Dollar)
  GBP: "en-GB", // United Kingdom (British Pound)
  JPY: "ja-JP", // Japanese Yen
  CAD: "en-CA", // Canadian Dollar
  AUD: "en-AU", // Australian Dollar
  CHF: "de-CH", // Swiss Franc
  SEK: "sv-SE", // Swedish Krona
  NOK: "no-NO", // Norwegian Krone
};

/**
 * Infers the locale based on the currency code.
 * @param {string} currency - The currency code (e.g., 'EUR', 'USD').
 * @returns {string} - The inferred locale.
 */
const getLocaleFromCurrency = (currency) => {
  return currencyToLocaleMap[currency] || "da-DK"; // Default to 'da-DK' if not found
};

/**
 * Formats the currency value based on the customer's active currency and inferred locale.
 * @param {number} amount - The price amount to format.
 * @param {string} currency - The currency code (e.g., 'EUR', 'USD').
 * @returns {string} - The formatted price with the appropriate currency symbol.
 */
const formatCurrency = (amount, currency) => {
  const locale = getLocaleFromCurrency(currency); // Infer locale from currency code
  const formattedCurrency = new Intl.NumberFormat(locale, {
    style: "currency",
    currency: currency,
  }).format(amount);

  // remove the trailing dot from kr. if present, exmaple: 1.000 kr. -> 1.000 kr
  if (formattedCurrency.endsWith(".")) {
    return formattedCurrency.slice(0, -1);
  }

  return formattedCurrency;
};

const generateHeaderBar = (lowestPrice, currentyUnit, baseWeightUnit, priceBreaks) => {
  const headerCell = document.createElement("th");
  headerCell.setAttribute("colspan", priceBreaks.length + 2);
  headerCell.id = "volume-pricing-grid-header";
  const formattedLowestPrice = formatCurrency(lowestPrice, currentyUnit) + `/${baseWeightUnit}`;
  headerCell.innerHTML = `
    WHOLESALE PRICES
    <span class="normal-font">from</span>
    <span class="italic-price">${formattedLowestPrice}</span>
  `;

  return headerCell;
};

/**
 * Populates the table headers with the quantity break points.
 * @param {QuantityRule} quantityRule - The quantity rule for the product.
 * @param {QuantityPriceBreak[]} priceBreaks - The quantity price breaks for the product.
 * @returns {HTMLTableRowElement} - The table row element containing the headers.
 */
const generateHeaders = (quantityRule, priceBreaks, baseWeightUnit) => {
  const headersRow = document.createElement("tr");

  // Spacer
  const blankHeader = document.createElement("th");
  headersRow.appendChild(blankHeader);

  // First breakpoint / minimum
  if (quantityRule.minimum != 1) {
    const header = document.createElement("th");
    header.innerText = convertAmountToAmoutWithUnit(quantityRule.minimum, baseWeightUnit);
    header.innerText += ` - `;

    headersRow.appendChild(header);
  } else {
    const header = document.createElement("th");
    header.innerText = convertAmountToAmoutWithUnit(quantityRule.increment, baseWeightUnit);
    header.innerText += ` - `;

    headersRow.appendChild(header);
  }

  // Next breakpoints
  priceBreaks.forEach((breakPoint, index) => {
    const th = document.createElement("th");
    th.innerText = `${convertAmountToAmoutWithUnit(breakPoint.minimumQuantity, baseWeightUnit)}`;
    th.innerText += ` - `;

    // If last entry and a maximum is defined
    if (priceBreaks.length - 1 === index && quantityRule.maximum) {
      th.innerText += `${convertAmountToAmoutWithUnit(quantityRule.maximum, baseWeightUnit)}`;
    }

    headersRow.appendChild(th);
  });

  return headersRow;
};

/**
 * Generates table entries for a given variant
 *
 * @param {ProductVariant} variant - variant object
 * @param {number} currencyRate - rate of currency
 * @param {string} currentyUnit - unit of currency
 * @param {string} baseWeightUnit - unit of weight
 * @param {QuantityPriceBreak[]} priceBreaks - list of price breaks
 * @returns {HTMLTableRowElement[]} table row list
 */
const generateEntries = (variants, currencyRate, currentyUnit, baseWeightUnit) => {
  const entries = [];
  for (const variant of variants) {
    const entry = document.createElement("tr");
    const priceBreaks = variant.quantityPriceBreaks;

    // First row - Variant name
    const variantCell = document.createElement("th");
    variantCell.innerText = formatDate(variant.title);
    entry.appendChild(variantCell);

    // First row - Base price
    const basePriceCell = document.createElement("th");
    basePriceCell.innerText = formatCurrency(variant.price.amount * currencyRate, currentyUnit) + `/${baseWeightUnit}`;
    entry.appendChild(basePriceCell);

    // Next rows - Quantity price breaks
    priceBreaks.forEach((breakPoint) => {
      const priceCell = document.createElement("th");
      const convertedPrice = breakPoint.price.amount * currencyRate;
      priceCell.innerText = formatCurrency(convertedPrice, currentyUnit) + `/${baseWeightUnit}`;

      entry.appendChild(priceCell);
    });

    entries.push(entry);
  }

  return entries;
};

/**
 *
 * @param {string} productId
 * @returns void
 */
const generatePriceGrid = async (productId) => {
  const volumePricingData = await getVolumePricing(productId);

  console.log("volumePricingData %o", volumePricingData);

  if (!volumePricingData) {
    console.error("Volume pricing data not found");
    return;
  }

  const quantityRule = volumePricingData.variants[0]?.quantityRule || null;
  const priceBreaks = volumePricingData.variants[0]?.quantityPriceBreaks || null;
  const currentyUnit = Shopify.currency.active;
  const currencyRate = Shopify.currency.rate;

  let baseWeightUnit;

  const header = document.getElementById("volume-pricing-grid-header");
  // Typical format: xx,xx CURRENCY/UNIT => 20,00 kr/kg
  const priceWithUnitText = header?.querySelectorAll("span")?.[1]?.innerText;
  baseWeightUnit = priceWithUnitText ? priceWithUnitText.split("/")[1]?.trim() : null;

  if (!baseWeightUnit) {
    console.error("Base weight unit not found, defaulting to g");
    baseWeightUnit = "g";
  }

  const variants = volumePricingData.variants.filter(
    (variant) => variant.title.toLocaleLowerCase().search("sample") === -1
  );

  if (volumePricingData.variants.length === 0) {
    console.error("Variant not found on related product, skipping.");
    return;
  }

  if (!priceBreaks) {
    console.warn("Price break not defined on related product, skipping.");
    return;
  }

  const lowestPrice = findLowestPrice(variants, currencyRate);
  const table = document.getElementsByClassName("volume-pricing-grid")[0] || null;

  if (!table) {
    console.error("Volume pricing grid not found");
    return;
  }

  // Clear any existing rows
  table.innerHTML = "";

  // Show table
  table.classList.remove("hidden");
  document.getElementById("decorative-text")?.classList.remove("hidden");

  const tableBody = document.createElement("tbody");
  const headerRow = document.createElement("tr");

  // Table header: "WHOLESALE PRICES"
  const headerCell = generateHeaderBar(lowestPrice, currentyUnit, baseWeightUnit, priceBreaks);

  headerRow.appendChild(headerCell);
  tableBody.appendChild(headerRow);

  // -------------------- HEADERS ----------------------

  const headersRow = generateHeaders(quantityRule, priceBreaks, baseWeightUnit);
  tableBody.append(headersRow);

  // -------------------- ENTRIES ----------------------

  const entryRows = generateEntries(variants, currencyRate, currentyUnit, baseWeightUnit, priceBreaks);
  tableBody.append(...entryRows);

  // Append table
  table.appendChild(tableBody);
};
